import * as React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { cx } from '../utils';
import { devWarn } from '../internal/devWarn';
import type { UnsafeProps } from '../escape-hatches';

export type SwitchIntent = 'default' | 'success' | 'error' | 'warning' | 'info';
export type SwitchSize = 'sm' | 'md';

export interface SwitchProps extends UnsafeProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Accessible label — required. Renders as visible text beside the track.
   *  When `labelHidden` is true, must be a string (used as aria-label). */
  label: ReactNode;
  /** Hide the visible label (still accessible via aria-label).
   *  Requires `label` to be a string when true. */
  labelHidden?: boolean;
  intent?: SwitchIntent;
  /** sm (36x20) for dense contexts; md (48x28) default. */
  size?: SwitchSize;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

export const Switch = React.forwardRef<HTMLLabelElement, SwitchProps>(function Switch(
  {
    checked,
    onChange,
    label,
    labelHidden,
    intent = 'default',
    size = 'md',
    disabled,
    className,
    style,
    unsafe_className,
    unsafe_style,
  },
  ref,
) {
  const intentClass = intent !== 'default' ? `j-switch-${intent}` : '';
  const sizeClass = size === 'sm' ? 'j-switch-sm' : '';

  if (labelHidden && typeof label !== 'string') {
    devWarn(
      '[JKit.Switch] labelHidden is true but label is not a string. ' +
        'The switch will have no accessible name. Pass a string label when using labelHidden.',
    );
  }

  return (
    <label
      ref={ref}
      className={cx('j-switch', intentClass, sizeClass, className, unsafe_className)}
      data-checked={checked ? 'true' : undefined}
      data-disabled={disabled ? 'true' : undefined}
      style={{ ...style, ...unsafe_style }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={labelHidden && typeof label === 'string' ? label : undefined}
      />
      <span className="j-switch-track">
        <span className="j-switch-thumb" />
      </span>
      {!labelHidden && <span className="j-switch-label">{label}</span>}
    </label>
  );
});
